import * as ScrollArea from "@radix-ui/react-scroll-area"
import gsap from "gsap"
import ScrollToPlugin from "gsap/ScrollToPlugin"
import { ReactComponent as SmallArrowRight } from "images/SmallArrowRight.svg"
import { useClientOnly } from "library/ClientOnly"
import UniversalLink from "library/Loader/UniversalLink"
import { TabletMobileOnly } from "library/breakpointUtils"
import { fmobile, fresponsive, ftablet } from "library/fullyResponsive"
import useMedia from "library/useMedia"
import { useResponsivePixels } from "library/viewportUtils"
import { Fragment, useRef, useState } from "react"
import styled, { css } from "styled-components"
import colors from "styles/colors"

gsap.registerPlugin(ScrollToPlugin)

export default function ScrollableArea({
	children,
	sidePadding: externalPad,
	lotsOfContent = false,
}: {
	sidePadding?: number
	children: React.ReactNode
	/**
	 * if true, will modify the scroll behavior on desktop and tablet
	 */
	lotsOfContent?: boolean
}) {
	const row = useRef<HTMLDivElement | null>(null)
	const scrollable = useRef<HTMLDivElement | null>(null)

	/**
	 * where is the scrollable area currently positioned?
	 */
	const [position, setPosition] = useState<"start" | "middle" | "end">("start")
	/**
	 * how much padding is there on the left and right of the scrollable area?
	 */
	const internalPadResp = useResponsivePixels(useMedia(19, 19, 19, 9))
	const externalPadResp = useResponsivePixels(externalPad ?? 0)
	const padSize =
		useClientOnly(externalPad ? externalPadResp : internalPadResp) ?? 0.5

	/**
	 * when the user clicks, should we scroll to the next card, or scroll the full width of the container?
	 */
	const defaultBehavior = useMedia("full", "full", "full", "card")
	const behavior = lotsOfContent ? "card" : defaultBehavior

	/**
	 * update position whenever the area is scrolled
	 */
	const onScroll: React.UIEventHandler<HTMLDivElement> = (e) => {
		const scrollArea = e.target as HTMLDivElement
		const scrollAreaWidth = scrollArea.clientWidth
		const scrollAreaScrollLeft = scrollArea.scrollLeft
		const scrollAreaScrollWidth = scrollArea.scrollWidth

		if (scrollAreaScrollLeft <= padSize) {
			setPosition("start")
		} else if (
			scrollAreaScrollLeft + scrollAreaWidth >=
			scrollAreaScrollWidth - padSize
		) {
			setPosition("end")
		} else {
			setPosition("middle")
		}
	}

	/**
	 * scroll the area whenever the buttons are clicked
	 */
	const onButton = (direction: "left" | "right") => {
		if (!scrollable.current || !row.current) return
		const cards = [...row.current.children]
		const cardWidth = cards[0]?.clientWidth ?? 0
		const containerPosition = scrollable.current.getBoundingClientRect().left

		if (behavior === "card") {
			// scroll to the first card that is right of cardWidth px
			const firstRightCard = cards.find(
				(card) =>
					card.getBoundingClientRect().left - containerPosition > cardWidth,
			)

			// or scroll to the last card that is left of padSize px
			const lastLeftCard = [...cards]
				.reverse()
				.find(
					(card) =>
						card.getBoundingClientRect().left - containerPosition < padSize,
				)

			gsap.to(scrollable.current, {
				scrollTo: {
					x: direction === "left" ? lastLeftCard : firstRightCard,
					autoKill: true,
					offsetX: padSize,
				},
				ease: "power2.inOut",
			})
		} else {
			gsap.to(scrollable.current, {
				scrollTo: {
					x:
						direction === "left"
							? 0 + padSize
							: scrollable.current.scrollWidth -
								scrollable.current.clientWidth +
								padSize,
					autoKill: true,
					offsetX: padSize,
				},
				ease: "power2.inOut",
			})
		}
	}

	const Wrapper = lotsOfContent ? Fragment : TabletMobileOnly

	return (
		<ScrollRoot type="always" style={{ "--padding": `${padSize}px` }}>
			<ScrollArea.Viewport onScroll={onScroll} ref={scrollable}>
				<Row $desktopPad={lotsOfContent ? padSize : 0} ref={row}>
					{children}
				</Row>
			</ScrollArea.Viewport>
			<Wrapper>
				<ScrollBarWrapper>
					<Scrollbar orientation="horizontal">
						<Thumb />
					</Scrollbar>
					<Button
						type="button"
						disabled={position === "start"}
						onClick={() => onButton("left")}
						ariaLabel="Scroll left"
					>
						<Arrow $right />
					</Button>
					<Button
						type="button"
						disabled={position === "end"}
						onClick={() => onButton("right")}
						ariaLabel="Scroll right"
					>
						<Arrow />
					</Button>
				</ScrollBarWrapper>
			</Wrapper>
		</ScrollRoot>
	)
}

const ScrollRoot = styled(ScrollArea.Root)`
	--radix-scroll-area-thumb-height: 1px;
	--radix-scroll-area-thumb-width: 1px;

	width: 100%;
	height: 100%;
	overflow: hidden;
`

const Row = styled.div<{
	$desktopPad: number
}>`
	display: flex;
	justify-content: space-between;

	& > * {
		flex-shrink: 0;
	}

	padding: 0 ${({ $desktopPad }) => $desktopPad}px;

	${fresponsive(css`
		gap: 20px;
		width: max-content;
	`)}

	${ftablet(css`
		padding: 0 var(--padding);
	`)}

  ${fmobile(css`
		gap: 14px;
		padding: 0 var(--padding);
	`)}
`

const ScrollBarWrapper = styled.div`
	${fresponsive(css`
		margin: 22px var(--padding) 0;
		display: flex;
		justify-content: end;
	`)}

	${fmobile(css`
		margin: 20px var(--padding) 0;
	`)}
`

const Scrollbar = styled(ScrollArea.Scrollbar)`
	${fresponsive(css`
		padding: 20px 0;
		position: relative !important;
		margin-right: 30px;
		width: 100%;
	`)}

	${fmobile(css`
		padding: 20px 0;
		margin: 0;
	`)}

  &::before {
		position: absolute;
		content: "";
		width: 100%;
		height: 1px;
		background: #c1c1d3;
		left: 0;

		${fresponsive(css`
			top: 20px;
		`)}
	}
`

const Thumb = styled(ScrollArea.Thumb)`
	background: ${colors.blue2};
`

const Button = styled(UniversalLink)<{
	disabled: boolean
}>`
	background: ${({ disabled }) => (disabled ? colors.grey1 : colors.blue2)};
	${fresponsive(css`
		width: 40px;
		height: 40px;
		border-radius: 8px;
		margin-left: 10px;
		display: grid;
		place-items: center;
		flex-shrink: 0;
	`)}
`

const Arrow = styled(SmallArrowRight)<{ $right?: boolean }>`
	rotate: ${({ $right }) => ($right ? "180deg" : "0deg")};
	${fresponsive(css`
		width: 12px;
		height: 12px;
	`)}
`
